/**
 * ticketEvidenceWorkflowModel — PROD-UX-070 (FULL-0156), MASTER_FLOW §14C.1 / §14C.3 / §14C.5.
 *
 * The technician's ticket-to-evidence path has four artifacts that must never collapse into one
 * another:
 *
 *   1. the ticket itself           — stored information, not a recommendation;
 *   2. the AI recommendation       — a proposal from the digital employee, not an approval;
 *   3. the action proposal         — a request that still needs a separate human approval;
 *   4. the verified outcome        — a measured result, never the model's own claim of success.
 *
 * This module derives those four stages from the run records the workbench already holds. It is
 * pure (no React, no data access, no API import), so the distinctions and the action gates are
 * testable without a DOM.
 *
 * TWO RULES THAT DECIDE EVERY ARM
 *
 *   - "Verified" is an evidence claim. A run is only shown as `verified` when a succeeded action
 *     row exists AND the run/context does not mark the delivery as unproven. A close action whose
 *     postcondition is proven also requires the ticket to be closed. Anything else (a terminal run
 *     with no succeeded action, an unknown delivery, an active run) is `warning`/`unknown` and says
 *     what is missing. The model never upgrades a reported success into a verified outcome.
 *   - "Actionable" is a freshness claim. Approving or starting is offered only when the run source
 *     is fresh AND the browser is online. A stale refresh (the last payload is still held) and an
 *     offline browser keep the content visible for reading but disable the action, with the reason
 *     on the action. This surface never performs the approval itself: its approval action only
 *     moves focus to the existing human review card, where `agentRunsApi` and the server-side gate
 *     remain the authority.
 *
 * The four source states reuse `statusVocabulary.SOURCE_STATES` so this surface cannot invent a
 * fifth kind of "not current": loading, fresh, stale (refresh failed after a payload), failed (no
 * payload).
 */
import { actionTypeLabel } from "./actionTypeVocabulary";
import { degradedAnalysisLabel } from "./degradedAnalysisVocabulary";
import { SOURCE_STATES } from "./statusVocabulary";

export const WORKFLOW_STAGE_KEYS = Object.freeze([
  "ticket",
  "ai_recommendation",
  "action_proposal",
  "verified_outcome",
]);

/**
 * The stage identity is fixed metadata, not derived per ticket: every technician sees the same
 * four claims in the same order, with the same statement of what each stage is and is not.
 */
export const WORKFLOW_STAGES = Object.freeze({
  ticket: Object.freeze({
    key: "ticket",
    step: 1,
    label: "הקריאה",
    authority: "מידע שנשמר בקריאה — אינו המלצה ואינו דורש אישור.",
  }),
  ai_recommendation: Object.freeze({
    key: "ai_recommendation",
    step: 2,
    label: "המלצת ה-AI",
    authority: "הצעה של העובד הדיגיטלי — אינה אישור ואינה ביצוע.",
  }),
  action_proposal: Object.freeze({
    key: "action_proposal",
    step: 3,
    label: "הפעולה המוצעת",
    authority: "בקשה לפעולה שנדרשה לאישור אנושי לפני ביצוע — אישור או ביצוע אינם אימות תוצאה.",
  }),
  verified_outcome: Object.freeze({
    key: "verified_outcome",
    step: 4,
    label: "התוצאה המאומתת",
    authority: "נמדדת מרשומת פעולה שבוצעה ותנאי הסיום — לא מהצהרת המודל.",
  }),
});

/**
 * Machine-readable statement of this surface's authority, mirroring the V2 role surfaces. The
 * workflow may explain and navigate; it may not approve, execute or grant anything.
 */
export const WORKFLOW_AUTHORITY = Object.freeze({
  presentationOnly: true,
  approvesAction: false,
  executesAction: false,
  grantsPermission: false,
  authorizesAction: false,
  backendAuthoritative: true,
});

const REVIEW_STATUSES = new Set(["awaiting_technician_review", "awaiting_approval"]);
const ACTIVE_STATUSES = new Set([
  "pending",
  "analyzing",
  "diagnosing",
  "connection_active",
  "executing",
  "waiting_for_customer_connection",
  "waiting_for_customer_answer",
  "paused",
]);
const TERMINAL_SUCCESS = new Set(["completed", "resolved"]);
const TERMINAL_FAILURE = new Set(["failed", "rejected"]);
// A run that is executing OR waiting on a human has not produced an outcome yet. Both are known
// states; neither may be reported as an unrecognized status.
const RUNNING_STATUSES = new Set([...ACTIVE_STATUSES, ...REVIEW_STATUSES]);
const KNOWN_STATUSES = new Set([
  ...RUNNING_STATUSES,
  ...TERMINAL_SUCCESS,
  ...TERMINAL_FAILURE,
  "approved",
  "executing",
]);

const asArray = (value) => (Array.isArray(value) ? value : []);
const text = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
};
const truncate = (value, max = 120) => {
  const clean = text(value);
  if (!clean) return null;
  return clean.length <= max ? clean : `${clean.slice(0, max - 1).trimEnd()}…`;
};
const timeValue = (iso) => {
  if (!iso) return 0;
  const parsed = new Date(/Z|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * The four source states for the run list. `hasData` is the caller's statement that a payload is
 * being held (runs.length > 0, or react-query's dataUpdatedAt > 0 for a successful empty load).
 * `updatedAt` is informational: when the payload has no timestamp this model still knows it holds
 * data, so it reports `stale` after a failed refresh rather than pretending the source is failed.
 */
export function classifyRunsSource({ loading = false, failed = false, hasData = false, updatedAt = 0 } = {}) {
  if (failed) {
    return hasData
      ? { key: SOURCE_STATES.stale, since: updatedAt > 0 ? updatedAt : null }
      : { key: SOURCE_STATES.failed, since: null };
  }
  if (hasData) return { key: SOURCE_STATES.fresh, since: updatedAt > 0 ? updatedAt : null };
  if (loading) return { key: SOURCE_STATES.loading, since: null };
  return { key: SOURCE_STATES.fresh, since: null };
}

// A run timeline also carries diagnostic rows (initial_analysis, TeamViewer lookups, ESET reads).
// Those say the diagnosis happened; they are NOT the outcome of the proposed action, and treating
// a successful read as a verified outcome would be exactly the overclaim this stage exists to
// prevent. Only state-changing/approved action tokens are candidates for stage 3 and stage 4.
const OUTCOME_ACTION_TYPES = new Set([
  "close_ticket",
  "escalate",
  "send_customer_message",
  "add_comment",
  "browser_work_execute",
  "run_diagnostic_script",
]);

const outcomeActionsOf = (run) => {
  const types = new Set(OUTCOME_ACTION_TYPES);
  const proposed = text(run?.analysis?.proposed_action?.action_type);
  if (proposed) types.add(proposed);
  const executed = text(run?.context?.execution_result?.action_type);
  if (executed) types.add(executed);
  return asArray(run?.actions).filter((action) => types.has(action?.action_type));
};

const succeededActions = (actions) => actions.filter((action) => action?.outcome === "succeeded");
const failedActions = (actions) => actions.filter((action) => action?.outcome === "failed");

/**
 * True when the run says an action executed but its own record does not prove the delivery or the
 * postcondition. `context.action_outcome` is written only on that branch by manager_agent; a
 * status other than `verified_success` is therefore an admission, not a success. The execution
 * result's explicit `delivery_verified: false` / unknown `mail_sent` are the same admission.
 */
function unprovenExecution(run) {
  const outcome = run?.context?.action_outcome;
  if (outcome && typeof outcome === "object") {
    if (outcome.status === "verified_success") return false;
    if (outcome.status != null) return true;
  }
  const execution = run?.context?.execution_result;
  if (execution && typeof execution === "object") {
    if (execution.success !== true || execution.executed !== true) return true;
    if (execution.delivery_verified === false) return true;
    if (Object.prototype.hasOwnProperty.call(execution, "mail_sent") && execution.mail_sent == null) {
      return true;
    }
  }
  return false;
}

const actionTypeOf = (action) =>
  text(action?.action_type) || text(action?.description) || null;

/**
 * The verified-outcome stage. The return value always carries an explicit `state`, an amount of
 * evidence and a `basis` sentence that says which fact was measured — a verified badge without a
 * basis is exactly the overclaim this stage exists to prevent.
 */
export function deriveVerifiedOutcome({ run, ticket }) {
  if (!run) {
    return {
      state: "unknown",
      stateLabel: "אין תוצאה",
      lines: ["טרם נוצרה תוצאה לאימות — אין ריצת AI לקריאה זו."],
      basis: null,
    };
  }

  const candidates = outcomeActionsOf(run);
  const succeeded = succeededActions(candidates);
  const failed = failedActions(candidates);
  const execution = run?.context?.execution_result;
  const unproven = succeeded.length > 0 && unprovenExecution(run);

  if (unproven) {
    return {
      state: "warning",
      stateLabel: "לא אומת",
      lines: [
        "הפעולה דווחה כבוצעה, אך המסירה או תנאי הסיום שלה אינם מאומתים.",
        "הצלחה מדווחת אינה אימות — יש לבדוק את התוצאה לפני סגירת הנושא.",
      ],
      basis: "בסיס האימות: קיים סימון מפורש של מסירה לא מאומתת או תוצאת שליחה לא ידועה.",
    };
  }

  // A failed run is not a verified outcome even if an EARLIER attempt in the same run left a
  // succeeded action row. The run's terminal status is the current fact; a prior success is
  // history, not the outcome of this run.
  if (failed.length > 0 || TERMINAL_FAILURE.has(run.status)) {
    const rejected = run.status === "rejected";
    return {
      state: "blocked",
      stateLabel: rejected ? "נדחתה" : "נכשלה",
      lines: [
        rejected
          ? "הריצה נדחתה — לא נוצרה תוצאה מאומתת."
          : "לא נוצרה תוצאה מאומתת — הפעולה או הריצה נכשלו.",
      ],
      basis: "בסיס האימות: לא קיימת רשומת פעולה שבוצעה בהצלחה בריצה שהסתיימה בהצלחה.",
    };
  }

  if (succeeded.length > 0) {
    const first = succeeded[0];
    const closeProven =
      execution &&
      execution.action_type === "close_ticket" &&
      execution.success === true &&
      execution.executed === true &&
      ticket?.status === "closed";
    const lines = [];
    const actionLabel = actionTypeLabel(actionTypeOf(first));
    if (actionLabel) lines.push(`סוג הפעולה: ${actionLabel}`);
    const description = truncate(first.description);
    if (description) lines.push(`פעולה שתועדה: ${description}`);
    return {
      state: "verified",
      stateLabel: "מאומת",
      lines: lines.length ? lines : ["תועדה פעולה שבוצעה בהצלחה."],
      basis: closeProven
        ? "בסיס האימות: סגירת הקריאה בוצעה בהצלחה והקריאה רשומה כסגורה."
        : "בסיס האימות: תועדה פעולה עם תוצאה מוצלחת ביומן הריצה, ללא סימון אי-אימות.",
    };
  }

  if (TERMINAL_SUCCESS.has(run.status)) {
    return {
      state: "warning",
      stateLabel: "לא אומת",
      lines: [
        "הריצה הסתיימה, אך לא תועדה פעולה שבוצעה ולכן אין תוצאה מאומתת.",
        "אין לסמן את הקריאה כנפתרה על סמך סיום הריצה בלבד.",
      ],
      basis: "בסיס האימות: חסרה רשומת פעולה עם תוצאה מוצלחת.",
    };
  }

  if (RUNNING_STATUSES.has(run.status)) {
    return {
      state: "unknown",
      stateLabel: "טרם הושלמה",
      lines: ["אין עדיין תוצאה לאימות — הריצה טרם הסתיימה."],
      basis: null,
    };
  }

  return {
    state: "unknown",
    stateLabel: "לא מזוהה",
    lines: ["מצב הריצה אינו מזוהה — אין לקבוע אם קיימת תוצאה מאומתת."],
    basis: null,
  };
}

const analysisLines = (analysis) => {
  const lines = [];
  if (!analysis || typeof analysis !== "object") return lines;
  const degraded = degradedAnalysisLabel(analysis.degraded);
  if (degraded) lines.push(degraded);
  const issue = text(analysis.likely_issue);
  if (issue) lines.push(`הממצא המרכזי: ${issue}`);
  const nextStep = text(analysis.recommended_next_step);
  if (nextStep) lines.push(`צעד מומלץ: ${nextStep}`);
  if (analysis.confidence != null && Number.isFinite(Number(analysis.confidence))) {
    lines.push(`ביטחון הניתוח: ${analysis.confidence}%`);
  }
  const missing = asArray(analysis.missing_information).map(text).filter(Boolean);
  if (missing.length > 0) lines.push(`חסר מידע: ${missing.join(" · ")}`);
  return lines;
};

const actionFor = ({ kind, label, runId = null, actionable, reason }) => ({
  kind,
  label,
  runId,
  disabled: !actionable,
  disabledReason: actionable ? null : reason,
});

const reasonForSource = (sourceKey, online) => {
  if (!online) return "אין חיבור לרשת — לא ניתן לאשר או להפעיל פעולה עד לחזרת החיבור.";
  if (sourceKey === SOURCE_STATES.stale) {
    return "הנתונים אינם נוכחיים — יש לרענן בהצלחה לפני אישור או הפעלה.";
  }
  if (sourceKey === SOURCE_STATES.loading) return "הנתונים עדיין נטענים — לא ניתן לפעול.";
  if (sourceKey === SOURCE_STATES.failed) return "נתוני הריצות אינם זמינים — לא ניתן לפעול.";
  return null;
};

/**
 * Derive the four stages, the current step and every action the surface may offer.
 *
 * `runs` is the array the workbench already received from `agentRunsApi.list(ticketId)`. The model
 * never fetches, approves, executes or invents a run.
 */
export function deriveTicketEvidenceWorkflow({
  ticket,
  technicianName = null,
  runs = [],
  source = null,
  online = false,
  canRun = false,
  canApprove = false,
} = {}) {
  const sourceKey = source?.key ?? SOURCE_STATES.loading;
  const sourceReason = reasonForSource(sourceKey, online);
  const actionable = sourceKey === SOURCE_STATES.fresh && online === true;

  const sortedRuns = [...asArray(runs)]
    .map((run) => run || {})
    .sort(
      (a, b) =>
        timeValue(b.created_at) - timeValue(a.created_at) ||
        String(b.id ?? "").localeCompare(String(a.id ?? ""))
    );
  const latestRun = sortedRuns[0] ?? null;
  const actions = outcomeActionsOf(latestRun);
  const reviewRun = sortedRuns.find((run) => REVIEW_STATUSES.has(run.status)) ?? null;
  const status = latestRun?.status ?? null;
  const storageIndeterminate =
    (sourceKey === SOURCE_STATES.failed || sourceKey === SOURCE_STATES.loading) && !latestRun;

  // ── Stage 1: the ticket ────────────────────────────────────────────────────
  const ticketLines = [];
  const ticketNumber = text(ticket?.number) || text(ticket?.ticket_number);
  const ticketSubject = text(ticket?.title) || text(ticket?.subject);
  if (ticketNumber) ticketLines.push(`מספר קריאה: ${ticketNumber}`);
  if (ticketSubject) ticketLines.push(`נושא הקריאה: ${ticketSubject}`);
  if (ticketLines.length === 0) ticketLines.push("פרטי הקריאה נטענו; אין כותרת להצגה.");
  if (technicianName) ticketLines.push(`טכנאי אחראי: ${technicianName}`);

  const ticketStage = {
    ...WORKFLOW_STAGES.ticket,
    state: "information",
    stateLabel: "מידע",
    lines: ticketLines,
    basis: null,
    action: null,
    denied: null,
    runId: null,
  };

  // ── Stage 2: the AI recommendation ─────────────────────────────────────────
  let recommendationStage;
  if (storageIndeterminate) {
    const loading = sourceKey === SOURCE_STATES.loading;
    recommendationStage = {
      ...WORKFLOW_STAGES.ai_recommendation,
      state: "unknown",
      stateLabel: loading ? "טוען" : "לא זמין",
      lines: [
        loading
          ? "טוען את נתוני הריצה — טרם ניתן לקבוע אם קיימת המלצה."
          : "נתוני הריצות לא נטענו — לא ניתן לקבוע אם קיימת המלצה.",
      ],
      basis: null,
      action: null,
      denied: null,
      runId: null,
    };
  } else if (!latestRun) {
    const denied = canRun
      ? null
      : {
          reason: "הפעלת ניתוח AI דורשת הרשאת הפעלה.",
          testId: "workflow-denied-ai_recommendation",
        };
    recommendationStage = {
      ...WORKFLOW_STAGES.ai_recommendation,
      state: "unknown",
      stateLabel: "אין עדיין",
      lines: ["אין עדיין המלצת AI לקריאה זו — הניתוח טרם הופעל."],
      basis: null,
      action: canRun
        ? actionFor({
            kind: "start",
            label: "הפעל ניתוח AI",
            actionable,
            reason: sourceReason,
          })
        : null,
      denied,
      runId: null,
    };
  } else if (REVIEW_STATUSES.has(status)) {
    const lines = analysisLines(latestRun.analysis);
    recommendationStage = {
      ...WORKFLOW_STAGES.ai_recommendation,
      state: "recommendation",
      stateLabel: "המלצה",
      lines: lines.length
        ? lines
        : ["הריצה ממתינה לסקירה, אך לא הוצגו בה ממצאים או צעד מומלץ."],
      basis: null,
      action: null,
      denied: null,
      runId: latestRun.id ?? null,
    };
  } else if (ACTIVE_STATUSES.has(status)) {
    const lines = analysisLines(latestRun.analysis);
    recommendationStage = {
      ...WORKFLOW_STAGES.ai_recommendation,
      state: lines.length ? "recommendation" : "unknown",
      stateLabel: lines.length ? "המלצה חלקית" : "טרם הושלמה",
      lines: lines.length
        ? [...lines, "ההמלצה טרם הושלמה ואינה מוכנה לאישור."]
        : ["העובד הדיגיטלי עדיין אוסף נתונים — ההמלצה טרם הושלמה."],
      basis: null,
      action: null,
      denied: null,
      runId: latestRun.id ?? null,
    };
  } else if (TERMINAL_FAILURE.has(status)) {
    const partial = analysisLines(latestRun.analysis);
    const denied = canRun
      ? null
      : {
          reason: "הרצת ניתוח חדש דורשת הרשאת הפעלה.",
          testId: "workflow-denied-ai_recommendation",
        };
    recommendationStage = {
      ...WORKFLOW_STAGES.ai_recommendation,
      state: "blocked",
      stateLabel: status === "rejected" ? "נדחתה" : "נכשלה",
      lines: [
        status === "rejected"
          ? "הריצה האחרונה נדחתה — אין המלצה תקפה לאישור."
          : "הריצה האחרונה נכשלה — אין המלצה תקפה לאישור.",
        ...(partial.length
          ? ["פרטים חלקיים מהריצה (אינם המלצה תקפה):", ...partial]
          : []),
      ],
      basis: null,
      action: canRun
        ? actionFor({
            kind: "start",
            label: "הרץ ניתוח חדש",
            actionable,
            reason: sourceReason,
          })
        : null,
      denied,
      runId: latestRun.id ?? null,
    };
  } else if (TERMINAL_SUCCESS.has(status) || status === "approved") {
    const lines = analysisLines(latestRun.analysis);
    recommendationStage = {
      ...WORKFLOW_STAGES.ai_recommendation,
      state: lines.length ? "recommendation" : "warning",
      stateLabel: lines.length ? "המלצה" : "ללא המלצה",
      lines: lines.length
        ? lines
        : ["הריצה הסתיימה ללא המלצה שאפשר להציג — יש לבדוק את הריצה המלאה."],
      basis: null,
      action: null,
      denied: null,
      runId: latestRun.id ?? null,
    };
  } else {
    recommendationStage = {
      ...WORKFLOW_STAGES.ai_recommendation,
      state: "unknown",
      stateLabel: "לא מזוהה",
      lines: ["מצב הריצה אינו מזוהה — אין להסיק ממנו המלצה או היעדר המלצה."],
      basis: null,
      action: null,
      denied: null,
      runId: latestRun.id ?? null,
    };
  }

  // ── Stage 3: the action proposal ───────────────────────────────────────────
  let proposalStage;
  if (storageIndeterminate) {
    const loading = sourceKey === SOURCE_STATES.loading;
    proposalStage = {
      ...WORKFLOW_STAGES.action_proposal,
      state: "unknown",
      stateLabel: loading ? "טוען" : "לא זמין",
      lines: [
        loading
          ? "טוען את נתוני הריצה — טרם ניתן לקבוע אם קיימת פעולה מוצעת."
          : "נתוני הריצות לא נטענו — לא ניתן לקבוע אם קיימת פעולה מוצעת.",
      ],
      basis: null,
      action: null,
      denied: null,
      runId: null,
    };
  } else if (reviewRun) {
    const proposed = reviewRun.analysis?.proposed_action;
    const lines = [];
    const type = text(proposed?.action_type);
    if (type) lines.push(`סוג הפעולה: ${actionTypeLabel(type)}`);
    const target = truncate(proposed?.target, 100);
    if (target) lines.push(`יעד הפעולה: ${target}`);
    lines.push("הפעולה טרם בוצעה; נדרש אישור אנושי נפרד לפני ביצוע.");
    proposalStage = {
      ...WORKFLOW_STAGES.action_proposal,
      state: type ? "approval_required" : "warning",
      stateLabel: type ? "דורש אישור" : "חסרה פעולה מוצעת",
      lines: type
        ? lines
        : ["הריצה ממתינה לסקירה, אך לא צוינה בה פעולה מוצעת."],
      basis: null,
      action: canApprove
        ? actionFor({
            kind: "focus_approval",
            label: type ? "עבור לפעולה לאישור" : "עבור לכרטיס הסקירה",
            runId: reviewRun.id ?? null,
            actionable,
            reason: sourceReason,
          })
        : null,
      denied: canApprove
        ? null
        : {
            reason: "לאישור הפעולה במסלול זה נדרשת הרשאת אישור פעולות AI.",
            testId: "workflow-denied-action_proposal",
          },
      runId: reviewRun.id ?? null,
    };
  } else if (status === "rejected") {
    const lines = [];
    const type = text(latestRun?.analysis?.proposed_action?.action_type);
    if (type) lines.push(`סוג הפעולה: ${actionTypeLabel(type)}`);
    lines.push("הפעולה המוצעת נדחתה ולא בוצעה.");
    proposalStage = {
      ...WORKFLOW_STAGES.action_proposal,
      state: "blocked",
      stateLabel: "נדחתה",
      lines,
      basis: null,
      action: null,
      denied: null,
      runId: latestRun?.id ?? null,
    };
  } else if (status === "approved" || status === "executing") {
    proposalStage = {
      ...WORKFLOW_STAGES.action_proposal,
      state: "approved",
      stateLabel: "אושרה — בביצוע",
      lines: ["הפעולה אושרה ומבוצעת; התוצאה טרם אומתה."],
      basis: null,
      action: null,
      denied: null,
      runId: latestRun?.id ?? null,
    };
  } else if (succeededActions(actions).length > 0) {
    const first = succeededActions(actions)[0];
    const type = actionTypeOf(first);
    proposalStage = {
      ...WORKFLOW_STAGES.action_proposal,
      state: "approved",
      stateLabel: "אושרה ובוצעה",
      lines: [
        ...(type ? [`סוג הפעולה: ${actionTypeLabel(type)}`] : []),
        "הפעולה אושרה ובוצעה; ההרשאה והביצוע תועדו ביומן הריצה.",
        "הביצוע לבדו אינו אימות — שלב התוצאה מציג את מה שנמדד.",
      ],
      basis: null,
      action: null,
      denied: null,
      runId: latestRun?.id ?? null,
    };
  } else if (failedActions(actions).length > 0 || status === "failed") {
    const type = actionTypeOf(failedActions(actions)[0] ?? {});
    proposalStage = {
      ...WORKFLOW_STAGES.action_proposal,
      state: "blocked",
      stateLabel: "נכשלה",
      lines: [
        ...(type ? [`סוג הפעולה: ${actionTypeLabel(type)}`] : []),
        "הפעולה המאושרת לא הושלמה — אין להציג אותה כבוצעה.",
      ],
      basis: null,
      action: null,
      denied: null,
      runId: latestRun?.id ?? null,
    };
  } else if (!latestRun) {
    proposalStage = {
      ...WORKFLOW_STAGES.action_proposal,
      state: "unknown",
      stateLabel: "אין פעולה",
      lines: ["אין עדיין פעולה מוצעת — היא תיווצר לאחר השלמת ההמלצה."],
      basis: null,
      action: null,
      denied: null,
      runId: null,
    };
  } else if (ACTIVE_STATUSES.has(status)) {
    proposalStage = {
      ...WORKFLOW_STAGES.action_proposal,
      state: "unknown",
      stateLabel: "טרם נוצרה",
      lines: ["הפעולה המוצעת תיווצר לאחר השלמת ההמלצה — אין עדיין בקשה לאישור."],
      basis: null,
      action: null,
      denied: null,
      runId: latestRun.id ?? null,
    };
  } else if (!KNOWN_STATUSES.has(status)) {
    proposalStage = {
      ...WORKFLOW_STAGES.action_proposal,
      state: "unknown",
      stateLabel: "לא מזוהה",
      lines: ["מצב הריצה אינו מזוהה — אין לקבוע מה הוצע או אושר."],
      basis: null,
      action: null,
      denied: null,
      runId: latestRun?.id ?? null,
    };
  } else {
    proposalStage = {
      ...WORKFLOW_STAGES.action_proposal,
      state: "unknown",
      stateLabel: "לא תועדה",
      lines: ["לא תועדה פעולה מוצעת או מאושרת בריצה האחרונה."],
      basis: null,
      action: null,
      denied: null,
      runId: latestRun?.id ?? null,
    };
  }

  // ── Stage 4: the verified outcome ──────────────────────────────────────────
  let outcomeStage;
  if (storageIndeterminate) {
    const loading = sourceKey === SOURCE_STATES.loading;
    outcomeStage = {
      ...WORKFLOW_STAGES.verified_outcome,
      state: "unknown",
      stateLabel: loading ? "טוען" : "לא זמין",
      lines: [
        loading
          ? "טוען את נתוני הריצה — טרם ניתן לקבוע אם קיימת תוצאה מאומתת."
          : "נתוני הריצות לא נטענו — לא ניתן לקבוע אם קיימת תוצאה מאומתת.",
      ],
      basis: null,
      action: null,
      denied: null,
      runId: null,
    };
  } else {
    const derived = deriveVerifiedOutcome({ run: latestRun, ticket });
    outcomeStage = {
      ...WORKFLOW_STAGES.verified_outcome,
      ...derived,
      action: latestRun?.id
        ? {
            kind: "open_run",
            label: "פתח את הריצה המלאה",
            runId: latestRun.id,
            disabled: false,
            disabledReason: null,
          }
        : null,
      denied: null,
      runId: latestRun?.id ?? null,
    };
  }

  const stages = [ticketStage, recommendationStage, proposalStage, outcomeStage];

  // The current step is where the operator's attention belongs. A finished run points at the
  // outcome; a pending review points at the proposal; everything before that points at the
  // recommendation. The ticket is context and is never marked as the active step.
  let currentStageKey = "ai_recommendation";
  if (reviewRun) currentStageKey = "action_proposal";
  else if (TERMINAL_SUCCESS.has(status)) currentStageKey = "verified_outcome";
  else if (status === "approved" || status === "executing") currentStageKey = "action_proposal";

  return {
    source: { key: sourceKey, since: source?.since ?? null },
    online: online === true,
    actionable,
    stages,
    currentStageKey,
    latestRunId: latestRun?.id ?? null,
    reviewRunId: reviewRun?.id ?? null,
  };
}
